import React, { useCallback, useEffect, useState } from 'react';
import cn from 'classnames';
import Image from '@components/Image';
import Modal from '@components/Modals';
import Action from '@components/Button/Action';
import Header from '@components/Modals/Header';
import Item from '@components/CheckMarkItem';
import styles from './Modal.module.scss';

import facebook from '@app/img/settings/facebook-dark.png';
import facebookLight from '@app/img/settings/facebook-light.png';
import { useSelector } from 'react-redux';
import {
  selectIntegrationByPlatform,
  selectIntegrationStatusOnDevice,
} from '@reducers/user/integrations';
import { syncDeviceIntegration } from '@actions/deviceIntegrations';

function Details({ onSync, forceDarkMode }) {
  return (
    <div className={styles.content}>
      <p className="text-secondary">
        We will seamlessly sync your Facebook friends and their birthdays to Clay in one easy step.
      </p>

      <Item title="Secure">
        All your friends from Facebook will be imported locally. Your friends information is
        encrypted end-to-end.
      </Item>
      <Item title="Private">
        Clay never has access to your Facebook username or password. Everything happens on device.
      </Item>

      <div className={styles.actions}>
        <Action onClick={onSync} block forceDarkMode={forceDarkMode}>
          Sync friends
        </Action>
        <div className={styles.description}>
          By syncing you agree to our{' '}
          <a className="link-inherit" href="#">
            privacy policy
          </a>
          .
        </div>
      </div>
    </div>
  );
}

function renderTitle(status, _progress, total) {
  if (status === 'waiting') {
    return 'Waiting for Facebook';
  }

  if (status === 'syncing') {
    return <>Syncing Friends&hellip;</>;
  }

  return `Successfully imported ${total} friends`;
}

export default function FacebookModal({ onClose, forceDarkMode }) {
  const integration = useSelector(selectIntegrationByPlatform('facebook'));
  const integrationStateOnDevice = useSelector(selectIntegrationStatusOnDevice('facebook'));
  const [status, setStatus] = useState<null | 'complete' | 'syncing' | 'waiting'>(null);

  const onSync = useCallback(() => {
    setStatus('waiting');
    syncDeviceIntegration('facebook');
  }, []);

  useEffect(() => {
    if (integrationStateOnDevice?.syncing && status !== 'syncing') {
      setStatus('syncing');
    } else if (!integrationStateOnDevice?.syncing && status === 'syncing') {
      setStatus('complete');
    }
  }, [integrationStateOnDevice?.syncing, status]);

  return (
    <Modal
      className={cn(styles.modalWrapper, 'modal-medium')}
      centered
      isOpen
      onRequestClose={onClose}
      forceDarkMode={forceDarkMode}
    >
      <Header.Plain onClose={onClose} />
      <div className={styles.content}>
        <Image
          className={styles.image}
          width={207}
          dark={facebook}
          light={forceDarkMode ? facebook : facebookLight}
        />

        {status === null ? (
          <Details onSync={onSync} forceDarkMode={forceDarkMode} />
        ) : (
          <>
            <h3 className="text-default">
              {renderTitle(
                status,
                integrationStateOnDevice?.progress || 0,
                integration?.status?.total,
              )}
            </h3>
            <p>
              {status === 'complete'
                ? 'You may now dismiss this screen.'
                : 'Please keep the Clay app open until the import is complete. You may close this modal.'}
            </p>
            <p>
              <a className="link-inherit" href="https://go.clay.earth/facebook" target="_blank">
                Learn more
              </a>{' '}
              about our Facebook integration.
            </p>
            <div className={styles.actions}>
              <Action onClick={onClose} block forceDarkMode={forceDarkMode}>
                Done
              </Action>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}
